import React from 'react';
import { Box, Button, Image, Typography } from '@lucid/lego';
import type { TransformedCheckoutDetail } from '../../../api/services/lupp/inventory/transformers.checkout';
import Feature from './Feature/Feature';
import Price from './Price/Price';
import styles from './VehicleCard.module.scss';

type Theme = 'dark' | 'light';

type PricingSummaryItem = {
  label: string;
  value: number;
};

type VehicleCardProps = {
  /** Theme for the component */
  theme: Theme;
  /** Checkout data from the API */
  checkoutData?: TransformedCheckoutDetail | null;
  /** Quote ID - required for download functionality */
  quoteId?: string;
  /** Callback when download quotation button is clicked */
  onDownloadQuotation?: () => void;
  /** Callback when send docusign button is clicked */
  onSendDocusign?: () => void;
  /** Current docusign status */
  docusignStatus?: string;
  /** Whether the quote has been saved successfully */
  isQuoteSaved?: boolean;
  /** Current status of the quote - buttons disabled when not 'Active' */
  currentStatus?: string;
  /** Test ID for testing */
  dataTestId?: string;
};

/**
 * Vehicle card component displaying vehicle configuration and pricing summary
 * @component
 * @param {VehicleCardProps} props - Component props
 * @returns {JSX.Element} Vehicle card with image, variant details, pricing, and action buttons
 * @example
 * <VehicleCard
 *   theme="dark"
 *   checkoutData={checkoutData}
 *   quoteId="quote-123"
 *   onDownloadQuotation={handleDownload}
 *   onSendDocusign={handleSendDocusign}
 *   dataTestId="vehicle-card"
 * />
 */

const VehicleCard: React.FC<VehicleCardProps> = ({
  theme,
  checkoutData,
  quoteId,
  onDownloadQuotation,
  onSendDocusign,
  docusignStatus,
  isQuoteSaved = false,
  currentStatus,
  dataTestId = 'vehicle-card',
}) => {
  const currencyCode = checkoutData?.currencyCode || 'USD';
  // Disable buttons when currentStatus exists and is not 'Active'
  const isNotActive = currentStatus ? currentStatus !== 'Active' : false;

  // Shared button state calculations
  const hasQuote = Boolean(quoteId || isQuoteSaved);
  const isDocusignAlreadySent =
    docusignStatus === 'Sent' || docusignStatus === 'DocuSign Sent' || docusignStatus === 'Shared';
  const isDocusignDisabled = !hasQuote || isNotActive || isDocusignAlreadySent;

  if (!checkoutData) {
    return null;
  }

  return (
    <Box className={styles.cardContainer} data-testid={dataTestId}>
      {/* Vehicle Image */}
      {checkoutData.vehicleImage && (
        <Box className={styles.vehicleImageContainer}>
          <Image
            uri_desktop={{ '1x': checkoutData.vehicleImage, '2x': checkoutData.vehicleImage }}
            uri_mobile={{ '1x': checkoutData.vehicleImage, '2x': checkoutData.vehicleImage }}
            alt={`${checkoutData.model} ${checkoutData.trim}`}
            className={styles.vehicleImage}
            aspectRatio={{ ratio: '2:1' }}
            rounded={false}
          />
        </Box>
      )}
      <Box>
        {/* Vehicle Variant Details */}
        {checkoutData?.vehicleOptions.map((option) => (
          <Feature
            key={option.code}
            description={option.name}
            price={option.value}
            currencyCode={currencyCode}
            theme={theme}
          />
        ))}

        {/* Features (sound system, driver assistance, packages, seating, etc.) */}
        {checkoutData?.features.map((feature) => (
          <Feature
            key={feature.code}
            description={feature.name}
            price={feature.value}
            currencyCode={currencyCode}
            theme={theme}
          />
        ))}

        {/* Divider */}
        <Box className={styles.divider} mt={16} mb={16} />
        {/* Subtotal */}
        <Box className={styles.subtotalRow} mt={8}>
          <Price
            label="Subtotal"
            value={checkoutData?.pricing?.subtotal ?? 0}
            currencyCode={currencyCode}
            theme={theme}
            isBold={true}
          />
        </Box>
      </Box>
      {/* Pricing Summary */}
      <Box className={styles.pricingSummary} mt={8}>
        {/* Divider */}
        <Box className={styles.divider} mt={16} mb={16} />
        <Price
          label={checkoutData.trim}
          value={checkoutData.pricing.basePrice}
          currencyCode={currencyCode}
          theme={theme}
        />
        <Price
          label="Add-Ons"
          value={checkoutData.pricing.addOns}
          currencyCode={currencyCode}
          theme={theme}
        />

        {/* Fees */}
        {checkoutData.pricing.fees.map((fee: PricingSummaryItem, index: number) => (
          <Price
            key={`fee-${index}`}
            label={fee.label}
            value={fee.value}
            currencyCode={currencyCode}
            theme={theme}
          />
        ))}

        {/* Credits */}
        {checkoutData.pricing.credits.map((credit: PricingSummaryItem, index: number) => (
          <Price
            key={`credit-${index}`}
            label={credit.label}
            value={credit.value}
            currencyCode={currencyCode}
            theme={theme}
            isNegative
          />
        ))}

        {/* Cash Discount */}
        {/* Divider */}
        {/* <Box className={styles.divider} mt={16} mb={16} />
        <SummaryRow
          label="Cash Discount"
          value={checkoutData.pricing.cashDiscount}
          currencyCode={currencyCode}
          theme={theme}
          isNegative
        /> */}

        {/* Divider */}
        <Box className={styles.divider} mt={16} mb={16} />

        {/* Total */}
        <Price
          label="Total*"
          value={checkoutData?.pricing.total}
          currencyCode={currencyCode}
          theme={theme}
          isBold
        />

        <Typography variant="body" size="small" color="gray-5" theme={theme}>
          * Total price including VAT, destination, roadworthy, delivery, documentation and
          registration costs.
        </Typography>
      </Box>
      {/* Action Buttons */}
      <Box className={styles.buttonContainer} pt={24} pb={24} pl={2} pr={2}>
        <Button
          label="Download Quotation"
          type="secondary"
          color={theme === 'dark' ? 'neutral' : 'brand'}
          size="medium"
          theme={theme === 'dark' ? 'light' : 'dark'}
          className={theme === 'light' ? styles.downloadButtonLight : undefined}
          handleClick={hasQuote ? onDownloadQuotation : undefined}
          disabled={!hasQuote}
          gaComponent="vehicle-card"
          dataTestId={`${dataTestId}-download-btn`}
        />
        <Button
          label="Send Docusign"
          type="primary"
          color={theme === 'dark' ? 'neutral' : 'brand'}
          size="medium"
          theme={theme === 'dark' ? 'light' : 'dark'}
          handleClick={!isDocusignDisabled ? onSendDocusign : undefined}
          disabled={isDocusignDisabled}
          gaComponent="vehicle-card"
          dataTestId={`${dataTestId}-send-btn`}
        />
      </Box>
    </Box>
  );
};

VehicleCard.displayName = 'VehicleCard';

export default VehicleCard;
